import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, Input, HostBinding, NgModule } from '@angular/core';
import { tuiCreateToken, tuiProvideOptions, tuiIsString, tuiPure } from '@taiga-ui/cdk';
import * as i1 from '@taiga-ui/core';
import { tuiSizeBigger, TuiSvgModule } from '@taiga-ui/core';
import { tuiStringHashToHsl } from '@taiga-ui/kit/utils/format';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@taiga-ui/kit/directives';
import { TuiLazyLoadingModule } from '@taiga-ui/kit/directives';

/** Default values for the avatar options. */
function TuiAvatarComponent_img_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "img", 3);
    i0.ɵɵlistener("error", function TuiAvatarComponent_img_0_Template_img_error_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onError());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("src", ctx_r1.avatarUrl || "", i0.ɵɵsanitizeUrl);
    i0.ɵɵattribute("alt", ctx_r1.text);
  }
}
function TuiAvatarComponent_tui_svg_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-svg", 4);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("src", ctx_r1.fallback);
  }
}
function TuiAvatarComponent_tui_svg_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-svg", 4);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("src", ctx_r1.stringAvatar);
  }
}
const TUI_AVATAR_DEFAULT_OPTIONS = {
  size: 'm',
  autoColor: false,
  rounded: false
};
/**
 * Default parameters for avatar component
 */
const TUI_AVATAR_OPTIONS = tuiCreateToken(TUI_AVATAR_DEFAULT_OPTIONS);
function tuiAvatarOptionsProvider(options) {
  return tuiProvideOptions(TUI_AVATAR_OPTIONS, options, TUI_AVATAR_DEFAULT_OPTIONS);
}
let TuiAvatarComponent = /*#__PURE__*/(() => {
  class TuiAvatarComponent {
    constructor(options) {
      this.options = options;
      this.size = this.options.size;
      this.text = '';
      this.fallback = null;
      this.autoColor = this.options.autoColor;
      this.rounded = this.options.rounded;
      this.avatarUrl = null;
      this.isUrlValid = false;
    }
    set avatarUrlSetter(avatarUrl) {
      this.avatarUrl = avatarUrl;
      this.isUrlValid = !!avatarUrl && !this.iconAvatar;
    }
    get bgColor() {
      return this.autoColor ? tuiStringHashToHsl(this.text) : '';
    }
    get hasAvatar() {
      return this.avatarUrl !== null && this.isUrlValid;
    }
    get iconAvatar() {
      var _a;
      return tuiIsString(this.avatarUrl) && !!((_a = this.avatarUrl) === null || _a === void 0 ? void 0 : _a.startsWith('tuiIcon'));
    }
    get useFallback() {
      return !!this.fallback && !!this.avatarUrl && !this.isUrlValid && !this.text.length;
    }
    get computedText() {
      return this.hasAvatar || this.iconAvatar || this.text === '' ? '' : this.getSlicedText(this.text, this.size);
    }
    get stringAvatar() {
      return this.iconAvatar ? String(this.avatarUrl) : '';
    }
    onError() {
      this.isUrlValid = false;
    }
    getSlicedText(text, size) {
      const words = text.split(' ');
      return words.length > 1 && tuiSizeBigger(size) ? words[0].slice(0, 1) + words[1].slice(0, 1) : words[0].slice(0, 1);
    }
  }
  TuiAvatarComponent.ɵfac = function TuiAvatarComponent_Factory(t) {
    return new (t || TuiAvatarComponent)(i0.ɵɵdirectiveInject(TUI_AVATAR_OPTIONS));
  };
  TuiAvatarComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: TuiAvatarComponent,
    selectors: [["tui-avatar"]],
    hostVars: 7,
    hostBindings: function TuiAvatarComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("data-size", ctx.size);
        i0.ɵɵstyleProp("background", ctx.bgColor);
        i0.ɵɵclassProp("_rounded", ctx.rounded)("_has-avatar", ctx.hasAvatar);
      }
    },
    inputs: {
      size: "size",
      avatarUrlSetter: [i0.ɵɵInputFlags.None, "avatarUrl", "avatarUrlSetter"],
      text: "text",
      fallback: "fallback",
      autoColor: "autoColor",
      rounded: "rounded"
    },
    decls: 5,
    vars: 4,
    consts: [["loading", "lazy", "class", "t-image", 3, "src", "error", 4, "ngIf"], ["class", "t-icon", 3, "src", 4, "ngIf"], [1, "t-text"], ["loading", "lazy", 1, "t-image", 3, "error", "src"], [1, "t-icon", 3, "src"]],
    template: function TuiAvatarComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵtemplate(0, TuiAvatarComponent_img_0_Template, 1, 2, "img", 0)(1, TuiAvatarComponent_tui_svg_1_Template, 1, 1, "tui-svg", 1)(2, TuiAvatarComponent_tui_svg_2_Template, 1, 1, "tui-svg", 1);
        i0.ɵɵelementStart(3, "span", 2);
        i0.ɵɵtext(4);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.isUrlValid);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", ctx.useFallback);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", ctx.stringAvatar);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(ctx.computedText);
      }
    },
    dependencies: [i1.TuiSvgComponent, i2.NgIf, i3.TuiLazyLoadingDirective],
    styles: ["[_nghost-%COMP%]{position:relative;display:flex;flex-shrink:0;border-radius:var(--tui-radius-m);color:var(--tui-secondary-active);text-align:center;text-transform:uppercase;justify-content:center;align-items:center;background:var(--tui-avatar-background, currentColor);-webkit-user-select:none;-moz-user-select:none;user-select:none;overflow:hidden}[data-size=xs][_nghost-%COMP%]{font:var(--tui-font-text-s);width:1.5rem;height:1.5rem}[data-size=s][_nghost-%COMP%]{font:var(--tui-font-text-m);width:2rem;height:2rem}[data-size=m][_nghost-%COMP%]{font:var(--tui-font-text-l);width:3rem;height:3rem}[data-size=l][_nghost-%COMP%]{font:var(--tui-font-heading-5);width:4rem;height:4rem;border-radius:var(--tui-radius-xl)}[data-size=xl][_nghost-%COMP%]{font:var(--tui-font-heading-3);width:6rem;height:6rem;border-radius:var(--tui-radius-xl)}[data-size=xxl][_nghost-%COMP%]{font:var(--tui-font-heading-1);width:9rem;height:9rem;border-radius:var(--tui-radius-l)}[new][data-size=xxs][_nghost-%COMP%]{width:1.5rem;height:1.5rem;border-radius:var(--tui-radius-s)}[new][data-size=xxs][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(.58)}[new][data-size=xs][_nghost-%COMP%]{width:2rem;height:2rem;border-radius:var(--tui-radius-s)}[new][data-size=xs][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(.83)}[new][data-size=s][_nghost-%COMP%]{width:2.5rem;height:2.5rem;border-radius:calc(1.5 * var(--tui-radius-s))}[new][data-size=s][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(1)}[new][data-size=m][_nghost-%COMP%]{width:3rem;height:3rem;border-radius:calc(2 * var(--tui-radius-s))}[new][data-size=m][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(1.17)}[new][data-size=l][_nghost-%COMP%]{width:4rem;height:4rem;border-radius:calc(2 * var(--tui-radius-s))}[new][data-size=l][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(1.67)}[new][data-size=xl][_nghost-%COMP%]{width:6rem;height:6rem;border-radius:calc(3 * var(--tui-radius-s))}[new][data-size=xl][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(2.33)}[new][data-size=xxl][_nghost-%COMP%]{width:8rem;height:8rem;border-radius:calc(4 * var(--tui-radius-s))}[new][data-size=xxl][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(3.33)}._has-avatar[_nghost-%COMP%]{background-color:transparent}._rounded[_nghost-%COMP%]{border-radius:100%!important}  .tui-avatar-stack{display:flex;width:-webkit-min-content;width:min-content}.tui-avatar-stack[data-size=xs][_nghost-%COMP%], .tui-avatar-stack   [data-size=xs][_nghost-%COMP%]{margin-right:-.625rem;box-shadow:0 0 0 1px var(--tui-avatar-border, var(--tui-base-01))}.tui-avatar-stack[data-size=s][_nghost-%COMP%], .tui-avatar-stack   [data-size=s][_nghost-%COMP%]{margin-right:-.875rem;box-shadow:0 0 0 1px var(--tui-avatar-border, var(--tui-base-01))}.tui-avatar-stack[data-size=m][_nghost-%COMP%], .tui-avatar-stack   [data-size=m][_nghost-%COMP%]{margin-right:-1.25rem;box-shadow:0 0 0 2px var(--tui-avatar-border, var(--tui-base-01))}.tui-avatar-stack[data-size=l][_nghost-%COMP%], .tui-avatar-stack   [data-size=l][_nghost-%COMP%]{margin-right:-1.625rem;box-shadow:0 0 0 2px var(--tui-avatar-border, var(--tui-base-01))}.tui-avatar-stack[data-size=xl][_nghost-%COMP%], .tui-avatar-stack   [data-size=xl][_nghost-%COMP%]{margin-right:-2.25rem;box-shadow:0 0 0 3px var(--tui-avatar-border, var(--tui-base-01))}.tui-avatar-stack[data-size=xxl][_nghost-%COMP%], .tui-avatar-stack   [data-size=xxl][_nghost-%COMP%]{margin-right:-3.375rem;box-shadow:0 0 0 3px var(--tui-avatar-border, var(--tui-base-01))}.t-image[_ngcontent-%COMP%]{width:100%;height:100%;object-fit:cover}.t-text[_ngcontent-%COMP%]{color:var(--tui-avatar-color, var(--tui-text-01))}.t-icon[_ngcontent-%COMP%]{position:absolute;top:0;left:0;width:100%;height:100%;color:var(--tui-avatar-color, var(--tui-text-01))}[data-size=xs][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(.5)}[data-size=m][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(1.6)}[data-size=l][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(2)}[data-size=xl][_nghost-%COMP%]   .t-icon[_ngcontent-%COMP%]{transform:scale(3.2)}"],
    changeDetection: 0
  });
  __decorate([tuiPure], TuiAvatarComponent.prototype, "getSlicedText", null);
  return TuiAvatarComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiAvatarModule = /*#__PURE__*/(() => {
  class TuiAvatarModule {}
  TuiAvatarModule.ɵfac = function TuiAvatarModule_Factory(t) {
    return new (t || TuiAvatarModule)();
  };
  TuiAvatarModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: TuiAvatarModule
  });
  TuiAvatarModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [[CommonModule, TuiLazyLoadingModule, TuiSvgModule]]
  });
  return TuiAvatarModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_AVATAR_DEFAULT_OPTIONS, TUI_AVATAR_OPTIONS, TuiAvatarComponent, TuiAvatarModule, tuiAvatarOptionsProvider };
